import { Outlet, createRootRouteWithContext, Link, useRouter } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, EmptyState, ErrorState, Toaster } from '@mkcore/ui';
import { useTheme } from '@/lib/theme';
import { useErrorMessage } from '@/lib/mutation';

export interface RouterContext {
  queryClient: QueryClient;
}

function Root() {
  const { resolved } = useTheme();
  return (
    <>
      <Outlet />
      <Toaster theme={resolved} />
    </>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <main className="mx-auto max-w-lg p-8">
      <EmptyState
        title={t('errors.notFoundPage')}
        action={
          <Button asChild variant="secondary">
            <Link to="/">{t('nav.home')}</Link>
          </Button>
        }
      />
    </main>
  );
}

/** Any error that no route handles, e.g. the API is unreachable while it restarts: a clear message and a retry. */
function RootError({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const router = useRouter();
  const message = useErrorMessage();
  return (
    <main className="mx-auto max-w-lg p-8">
      <ErrorState
        title={t('errors.generic')}
        description={message(error)}
        action={
          <Button variant="secondary" onClick={() => void router.invalidate()}>
            {t('common.retry')}
          </Button>
        }
      />
    </main>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
  notFoundComponent: NotFound,
  errorComponent: RootError,
});
